import type { UseVippsButtonProps } from '@maestro/commerce-blocks/components/vipps-button/vipps-button.types'

import { signIn } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import { useNotificationContext } from '@maestro/commerce-blocks/components/notification/notification.context'
import { createLogger } from '@maestro/core/logger'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:commerce-blocks:vipps-button')

const VIPPS_CHECKOUT_PAYMENT_STATUS_KEY = 'vippsCheckoutPaymentStatus'

export function useVippsButton({
  redirectTo,
  onSuccess,
  onError,
  providerId = 'vipps',
}: UseVippsButtonProps) {
  const {
    afterLogin: { redirectToUrl },
    urls,
  } = theme()
  const { showError } = useNotificationContext()
  const t = useTranslations('commerce-blocks.cart.vipps')
  const pathname = usePathname()

  const isSigningIn = useRef(false)

  const handleVippsAuth = useCallback(async () => {
    if (isSigningIn.current) {
      return
    }
    isSigningIn.current = true
    try {
      await signIn(providerId, {
        redirect: true,
        redirectTo: redirectTo || redirectToUrl,
      })
      onSuccess?.()
    } catch (error) {
      isSigningIn.current = false
      log.error('[useVippsButton] Authentication failed:', error)
      const errorObj =
        error instanceof Error ? error : new Error('Authentication failed')
      onError?.(errorObj)
    }
  }, [providerId, redirectTo, redirectToUrl, onSuccess, onError])

  useEffect(() => {
    if (pathname !== urls.cart) {
      return
    }
    const status = sessionStorage.getItem(VIPPS_CHECKOUT_PAYMENT_STATUS_KEY)
    if (!status) {
      return
    }
    const errorMessage =
      status === 'CANCELLED'
        ? t('vipps-status.cancelled')
        : t('vipps-status.other', { status })
    showError(errorMessage)
    sessionStorage.removeItem(VIPPS_CHECKOUT_PAYMENT_STATUS_KEY)
  }, [pathname, showError, t, urls.cart])

  return {
    handleVippsAuth,
    isSigningIn,
  }
}
